import React from 'react';

const Section12TechStack = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-slate-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">⚙️</span>
      <span className="bg-gradient-to-r from-slate-600 to-gray-600 bg-clip-text text-transparent">
        Tech Stack
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">What powers VowAI under the hood</p>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {/* AI / ML */}
      <div className="bg-gradient-to-br from-purple-50 to-violet-50/50 rounded-2xl p-5 border border-purple-200/50 hover:scale-105 transition-transform duration-300 hover:shadow-lg">
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">🧠</span>
          <h3 className="font-bold text-gray-800 text-sm">AI / Machine Learning</h3>
        </div>
        <div className="space-y-2">
          {[
            { name: 'Gemini 2.0 Flash', role: 'Theme generation · highest quality scores' },
            { name: 'Claude 3.5 Sonnet', role: 'Theme generation · most creative output' },
            { name: 'GPT-4o-mini', role: 'Theme generation · VowBot agent · embeddings' },
            { name: 'OpenAI Embeddings', role: 'text-embedding-3-small · 1536 dimensions' },
            { name: 'pgvector', role: 'Vector similarity search · IVFFlat indexing' },
            { name: 'Cosine Similarity', role: '6-dimensional vendor matching · 50ms' },
          ].map((tech, i) => (
            <div key={i} className="flex items-start">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 mr-2 flex-shrink-0"></span>
              <div>
                <span className="text-sm font-bold text-gray-800">{tech.name}</span>
                <span className="text-xs text-gray-400 ml-1">· {tech.role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Backend */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50/50 rounded-2xl p-5 border border-emerald-200/50 hover:scale-105 transition-transform duration-300 hover:shadow-lg">
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">🖥️</span>
          <h3 className="font-bold text-gray-800 text-sm">Backend</h3>
        </div>
        <div className="space-y-2">
          {[
            { name: 'Node.js + Express', role: 'REST API server · route handling' },
            { name: 'Aurora PostgreSQL', role: 'Primary database · managed AWS' },
            { name: 'pgvector Extension', role: 'Vector storage · similarity search' },
            { name: 'JWT Authentication', role: '7-day tokens · session management' },
            { name: 'bcrypt', role: 'Password hashing · 12 salt rounds' },
            { name: 'AWS SES', role: 'Transactional email · password reset' },
          ].map((tech, i) => (
            <div key={i} className="flex items-start">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 mr-2 flex-shrink-0"></span>
              <div>
                <span className="text-sm font-bold text-gray-800">{tech.name}</span>
                <span className="text-xs text-gray-400 ml-1">· {tech.role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Frontend */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50/50 rounded-2xl p-5 border border-blue-200/50 hover:scale-105 transition-transform duration-300 hover:shadow-lg">
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">🎨</span>
          <h3 className="font-bold text-gray-800 text-sm">Frontend</h3>
        </div>
        <div className="space-y-2">
          {[
            { name: 'React 18', role: 'SPA · hooks · functional components' },
            { name: 'Tailwind CSS', role: 'Utility-first styling · custom design system' },
            { name: 'Chart.js', role: 'Evaluation & A/B testing dashboards' },
            { name: 'React Router', role: 'Client-side routing · 20+ pages' },
            { name: 'Context API', role: 'Auth state · theme management · global state' },
            { name: 'localStorage / sessionStorage', role: 'Chat persistence · dedup tracking' },
          ].map((tech, i) => (
            <div key={i} className="flex items-start">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 mr-2 flex-shrink-0"></span>
              <div>
                <span className="text-sm font-bold text-gray-800">{tech.name}</span>
                <span className="text-xs text-gray-400 ml-1">· {tech.role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Infrastructure */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50/50 rounded-2xl p-5 border border-amber-200/50 hover:scale-105 transition-transform duration-300 hover:shadow-lg">
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">☁️</span>
          <h3 className="font-bold text-gray-800 text-sm">AWS Infrastructure</h3>
        </div>
        <div className="space-y-2">
          {[
            { name: 'Elastic Beanstalk', role: 'Backend deployment · auto-scaling' },
            { name: 'AWS Amplify', role: 'Frontend CI/CD · auto-deploy from Git' },
            { name: 'Aurora PostgreSQL', role: 'Managed database · pgvector enabled' },
            { name: 'Amazon SES', role: 'Email delivery · DKIM verified' },
            { name: 'CloudFlare', role: 'DNS · SSL · CDN · DDoS protection' },
            { name: 'Route 53', role: 'Domain management' },
          ].map((tech, i) => (
            <div key={i} className="flex items-start">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 mr-2 flex-shrink-0"></span>
              <div>
                <span className="text-sm font-bold text-gray-800">{tech.name}</span>
                <span className="text-xs text-gray-400 ml-1">· {tech.role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data & Analytics */}
      <div className="bg-gradient-to-br from-rose-50 to-pink-50/50 rounded-2xl p-5 border border-rose-200/50 hover:scale-105 transition-transform duration-300 hover:shadow-lg">
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">📊</span>
          <h3 className="font-bold text-gray-800 text-sm">Data & Analytics</h3>
        </div>
        <div className="space-y-2">
          {[
            { name: 'Unified Feedback Table', role: 'Ratings, thumbs, saves, time, navigation' },
            { name: 'Session Tracking', role: 'Heartbeat-based · tab visibility · cross-tab' },
            { name: 'Conversion Funnels', role: 'Vendor & ritual · per-theme dedup' },
            { name: 'A/B Test Results', role: 'Pooled t-test · Cohen\'s d · CI' },
            { name: 'Quality Scoring', role: '5-dimension automated evaluation' },
            { name: 'Cost Tracking', role: 'Per-model token & USD tracking' },
          ].map((tech, i) => (
            <div key={i} className="flex items-start">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 mr-2 flex-shrink-0"></span>
              <div>
                <span className="text-sm font-bold text-gray-800">{tech.name}</span>
                <span className="text-xs text-gray-400 ml-1">· {tech.role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security & Auth */}
      <div className="bg-gradient-to-br from-red-50 to-rose-50/50 rounded-2xl p-5 border border-red-200/50 hover:scale-105 transition-transform duration-300 hover:shadow-lg">
        <div className="flex items-center mb-4">
          <span className="text-2xl mr-3">🔐</span>
          <h3 className="font-bold text-gray-800 text-sm">Security & Auth</h3>
        </div>
        <div className="space-y-2">
          {[
            { name: 'JWT Tokens', role: '7-day expiry · session table tracking' },
            { name: 'bcrypt Hashing', role: '12 rounds · password strength validation' },
            { name: 'Rate Limiting', role: 'Per-endpoint · auth-specific stricter limits' },
            { name: 'CORS', role: 'Origin whitelist · credential support' },
            { name: 'Password Reset', role: 'SES email · 5-min token expiry · same-password check' },
            { name: 'Input Validation', role: 'Disposable email blocking · sanitization' },
          ].map((tech, i) => (
            <div key={i} className="flex items-start">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 mr-2 flex-shrink-0"></span>
              <div>
                <span className="text-sm font-bold text-gray-800">{tech.name}</span>
                <span className="text-xs text-gray-400 ml-1">· {tech.role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>

);

export default Section12TechStack;
